import type { JSX } from 'react';
import type { Palette } from '../_theme/palette.js';
import { hexWithAlpha } from '../_theme/palette.js';
import { DUR, EASE } from '../motion/tokens.js';

interface SegmentedProgressProps {
  palette: Palette;
  segmentCount: number;
  activeIndex: number;
}

export function SegmentedProgress({
  palette,
  segmentCount,
  activeIndex,
}: SegmentedProgressProps): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 4, padding: '10px 20px', background: palette.primarySoft }}>
      {Array.from({ length: segmentCount }, (_, index) => (
        <div
          key={index}
          style={{
            flex: 1,
            height: 4,
            borderRadius: 999,
            background: index <= activeIndex ? palette.primary : hexWithAlpha(palette.primary, 0.2),
            transition: `background ${DUR.base}ms ${EASE.out}`,
          }}
        />
      ))}
    </div>
  );
}

interface LinearProgressProps {
  palette: Palette;
  ratio: number;
  // When true, render an indeterminate sliding pulse instead of a fill.
  // Use during submit / network round-trips when there's no progress %
  // available but we still want to communicate "something is happening".
  indeterminate?: boolean;
}

export function LinearProgress({ palette, ratio, indeterminate }: LinearProgressProps): JSX.Element {
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <div
      style={{
        height: 3,
        background: hexWithAlpha(palette.primary, 0.15),
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {indeterminate ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '30%',
            height: '100%',
            background: palette.primary,
            borderRadius: 999,
            animation: `chirp-progress-pulse 1.4s ${EASE.standard} infinite`,
          }}
        />
      ) : (
        <div
          style={{
            width: `${Math.round(clamped * 100)}%`,
            height: '100%',
            background: palette.primary,
            transition: `width ${DUR.progress}ms ${EASE.out}`,
          }}
        />
      )}
    </div>
  );
}
